import { renderJsonTo } from '@/lib/zy-tiptap-utils-core';
import type { Extensions } from '@tiptap/core';
import type { JSONContent } from '@tiptap/react';

/** Matches export guid assignment in to-zybooks-json processNode. */
export function itlabContentResourceGuid(sectionId: string, labNodeId: string): string {
    return `${sectionId}_${labNodeId}`;
}

export interface ZyITLabFilePayload {
    url: string;
    altText?: string;
}

export interface ZyITLabVmImageTestPayload {
    run: boolean;
    command: string;
    expected: string;
}

export interface ZyITLabVmImagePayload {
    name: string;
    title: string;
    instanceType: string;
    startupCommands?: string;
}

export type ZyITLabItemPayload =
    | {
          pageBreak: true;
          files: ZyITLabFilePayload[];
          instructions: string;
          taskDescription: string;
      }
    | {
          step: true;
          number: number;
          files: ZyITLabFilePayload[];
          instructions: string;
      }
    | {
          goal: true;
          points: number;
          message: string;
          vmImageTests: ZyITLabVmImageTestPayload[];
      };

function serializeZyITLabMarkdownContent(node: JSONContent, extensions: Extensions): string {
    return (node.content || [])
        .map(child => renderJsonTo(child, extensions, 'html'))
        .join('')
        .trim();
}

function serializeInstructionsChild(parent: JSONContent, instructionsType: string, extensions: Extensions): string {
    const instructionsNode = (parent.content || []).find(child => child.type === instructionsType);
    if (!instructionsNode) {
        return '';
    }
    return serializeZyITLabMarkdownContent(instructionsNode, extensions);
}

export function buildZyITLabPayload(
    node: JSONContent,
    extensions: Extensions,
): {
    files: ZyITLabFilePayload[];
    items: ZyITLabItemPayload[];
    vmImages: ZyITLabVmImagePayload[];
    modelSolution: string;
    parts: number;
    instructions: string;
} {
    const children = node.content || [];
    const vmImagesIndex = children.findIndex(child => child.type === 'zyITLabVmImages');
    let instructions = '';
    let modelSolution = '';
    const vmImages: ZyITLabVmImagePayload[] = [];
    const items: ZyITLabItemPayload[] = [];
    let parts = 0;

    for (let childIndex = 0; childIndex < children.length; childIndex++) {
        const child = children[childIndex];
        if (child.type === 'zyITLabInstructions') {
            instructions = serializeZyITLabMarkdownContent(child, extensions);
            continue;
        }

        if (child.type === 'zyITLabModelSolution') {
            modelSolution = serializeZyITLabMarkdownContent(child, extensions);
            continue;
        }

        if (child.type === 'zyITLabVmImages') {
            for (const vm of child.content || []) {
                if (vm.type !== 'zyITLabVmImage') {
                    continue;
                }
                const vmImage: ZyITLabVmImagePayload = {
                    name: vm.attrs?.name || '',
                    title: vm.attrs?.title || '',
                    instanceType: vm.attrs?.instanceType || 'small',
                };
                if (vm.attrs?.startupCommands) {
                    vmImage.startupCommands = vm.attrs.startupCommands;
                }
                vmImages.push(vmImage);
            }
            continue;
        }

        if (child.type !== 'zyITLabTask') {
            continue;
        }

        const isOptionalTask = vmImagesIndex >= 0 && childIndex < vmImagesIndex;

        if (!isOptionalTask) {
            items.push({
                pageBreak: true,
                files: [],
                instructions: serializeInstructionsChild(child, 'zyITLabTaskInstructions', extensions),
                taskDescription: child.attrs?.taskDescription || '',
            });
        }

        let stepNumber = 1;
        for (const step of child.content || []) {
            if (step.type !== 'zyITLabStep') {
                continue;
            }

            items.push({
                step: true,
                number: stepNumber,
                files: [],
                instructions: serializeInstructionsChild(step, 'zyITLabStepInstructions', extensions),
            });
            stepNumber += 1;

            for (const goal of step.content || []) {
                if (goal.type !== 'zyITLabGoal') {
                    continue;
                }

                const points = Number(goal.attrs?.points) || 0;
                parts += points;

                items.push({
                    goal: true,
                    points,
                    message: goal.attrs?.message || '',
                    vmImageTests: (goal.content || [])
                        .filter(test => test.type === 'zyITLabVmImageTest')
                        .map(test => ({
                            run: Boolean(test.attrs?.run),
                            command: String(test.attrs?.command ?? ''),
                            expected: String(test.attrs?.expected ?? ''),
                        })),
                });
            }
        }
    }

    return {
        files: [],
        items,
        vmImages,
        modelSolution,
        parts,
        instructions,
    };
}
